/**
 * WallZ Lab — Core theme utilities.
 * Loaded on every page with `defer`. Component-specific behavior (header,
 * product page, cart, etc.) lives in their own asset files and reuses the
 * helpers exposed on `window.WallZ` here.
 */
(function () {
  'use strict';

  window.WallZ = window.WallZ || {};

  /* ---------------------------------------------------------------------
   * PubSub — lightweight event bus so independent sections (header cart
   * bubble, cart drawer, upsell blocks) can react to the same event
   * (e.g. "cart:updated") without direct references to each other.
   * ------------------------------------------------------------------- */
  var subscribers = {};
  function subscribe(eventName, callback) {
    if (!subscribers[eventName]) subscribers[eventName] = [];
    subscribers[eventName].push(callback);
    return function unsubscribe() {
      subscribers[eventName] = subscribers[eventName].filter(function (cb) { return cb !== callback; });
    };
  }
  function publish(eventName, data) {
    (subscribers[eventName] || []).forEach(function (cb) { cb(data); });
    document.dispatchEvent(new CustomEvent(eventName, { detail: data, bubbles: true }));
  }
  WallZ.PubSub = { subscribe: subscribe, publish: publish };

  /* ---------------------------------------------------------------------
   * Generic helpers
   * ------------------------------------------------------------------- */
  WallZ.utils = {
    debounce: function (fn, wait) {
      var t;
      return function () {
        var args = arguments, ctx = this;
        clearTimeout(t);
        t = setTimeout(function () { fn.apply(ctx, args); }, wait);
      };
    },
    throttle: function (fn, limit) {
      var inThrottle;
      return function () {
        var args = arguments, ctx = this;
        if (!inThrottle) {
          fn.apply(ctx, args);
          inThrottle = true;
          setTimeout(function () { inThrottle = false; }, limit);
        }
      };
    },
    formatMoney: function (cents, format) {
      if (typeof cents === 'string') cents = cents.replace('.', '');
      var value = '';
      var placeholder = /\{\{\s*(\w+)\s*\}\}/;
      var money_format = format || (window.WallZ.moneyFormat || '₹{{amount}}');
      function defaultTo(a, b) { return a === 0 ? a : a || b; }
      function formatWithDelimiters(number, precision, thousands, decimal) {
        precision = defaultTo(precision, 2);
        thousands = defaultTo(thousands, ',');
        decimal = defaultTo(decimal, '.');
        if (isNaN(number) || number == null) return 0;
        number = (number / 100.0).toFixed(precision);
        var parts = number.split('.'),
          dollars = parts[0].replace(/(\d)(?=(\d{3})+(?!\d))/g, '$1' + thousands),
          cents2 = parts[1] ? decimal + parts[1] : '';
        return dollars + cents2;
      }
      switch (money_format.match(placeholder)?.[1]) {
        case 'amount': value = formatWithDelimiters(cents, 2); break;
        case 'amount_no_decimals': value = formatWithDelimiters(cents, 0); break;
        default: value = formatWithDelimiters(cents, 2);
      }
      return money_format.replace(placeholder, value);
    },
    trapFocus: function (container, focusTarget) {
      var elements = container.querySelectorAll('button, [href], input, select, textarea, [tabindex]:not([tabindex="-1"])');
      var first = elements[0], last = elements[elements.length - 1];
      (focusTarget || first || container).focus();
      function handle(e) {
        if (e.key !== 'Tab') return;
        if (e.shiftKey && document.activeElement === first) { e.preventDefault(); last.focus(); }
        else if (!e.shiftKey && document.activeElement === last) { e.preventDefault(); first.focus(); }
      }
      container.addEventListener('keydown', handle);
      container._trapFocusHandler = handle;
    },
    removeTrapFocus: function (container) {
      if (container && container._trapFocusHandler) container.removeEventListener('keydown', container._trapFocusHandler);
    },
    fetchConfig: function (type) {
      return {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
      };
    },
    getStorage: function (key, fallback) {
      try {
        var raw = localStorage.getItem(key);
        return raw ? JSON.parse(raw) : fallback;
      } catch (e) { return fallback; }
    },
    setStorage: function (key, value) {
      try { localStorage.setItem(key, JSON.stringify(value)); } catch (e) {}
    },
  };

  /* ---------------------------------------------------------------------
   * Sticky header — condense on scroll, hide on scroll-down / reveal on
   * scroll-up (keeps cart + search reachable without eating viewport).
   * ------------------------------------------------------------------- */
  function initStickyHeader() {
    var header = document.querySelector('[data-site-header]');
    if (!header) return;
    var lastY = window.scrollY;
    var ticking = false;

    function onScroll() {
      var y = window.scrollY;
      header.classList.toggle('is-condensed', y > 40);
      if (y > 160 && y > lastY) {
        header.classList.add('is-hidden');
      } else {
        header.classList.remove('is-hidden');
      }
      lastY = y;
      ticking = false;
    }
    window.addEventListener('scroll', function () {
      if (!ticking) {
        requestAnimationFrame(onScroll);
        ticking = true;
      }
    }, { passive: true });
  }

  /* ---------------------------------------------------------------------
   * Scroll reveal — IntersectionObserver driven fade/slide-up for anything
   * marked [data-reveal] or [data-reveal-stagger]. CSS holds the keyframes.
   * ------------------------------------------------------------------- */
  function initScrollReveal() {
    if (document.documentElement.classList.contains('no-animations')) {
      document.querySelectorAll('[data-reveal], [data-reveal-stagger]').forEach(function (el) {
        el.classList.add('is-revealed');
      });
      return;
    }
    var targets = document.querySelectorAll('[data-reveal], [data-reveal-stagger]');
    if (!('IntersectionObserver' in window) || !targets.length) {
      targets.forEach(function (el) { el.classList.add('is-revealed'); });
      return;
    }
    var observer = new IntersectionObserver(function (entries, obs) {
      entries.forEach(function (entry) {
        if (entry.isIntersecting) {
          entry.target.classList.add('is-revealed');
          obs.unobserve(entry.target);
        }
      });
    }, { threshold: 0.12, rootMargin: '0px 0px -60px 0px' });
    targets.forEach(function (el) { observer.observe(el); });
  }

  /* ---------------------------------------------------------------------
   * Announcement bar — dismissible, remembers state for the session and
   * rotates multiple messages if more than one is configured.
   * ------------------------------------------------------------------- */
  function initAnnouncementBar() {
    var bar = document.querySelector('[data-announcement-bar]');
    if (!bar) return;
    var slides = bar.querySelectorAll('[data-announcement-slide]');
    if (slides.length > 1) {
      var i = 0;
      setInterval(function () {
        slides[i].classList.remove('is-active');
        i = (i + 1) % slides.length;
        slides[i].classList.add('is-active');
      }, 4200);
    }
  }

  /* ---------------------------------------------------------------------
   * Mobile nav drawer toggle (hamburger <-> off-canvas menu).
   * ------------------------------------------------------------------- */
  function initMobileNav() {
    var toggles = document.querySelectorAll('[data-mobile-nav-toggle]');
    var drawer = document.querySelector('[data-mobile-nav-drawer]');
    var overlay = document.querySelector('[data-overlay]');
    if (!toggles.length || !drawer) return;
    function close() {
      drawer.classList.remove('is-open');
      toggles.forEach(function (t) { t.setAttribute('aria-expanded', 'false'); });
      document.body.classList.remove('no-scroll');
      WallZ.utils.removeTrapFocus(drawer);
      if (overlay) overlay.classList.remove('is-visible');
    }
    function open() {
      drawer.classList.add('is-open');
      toggles.forEach(function (t) { t.setAttribute('aria-expanded', 'true'); });
      document.body.classList.add('no-scroll');
      WallZ.utils.trapFocus(drawer);
      if (overlay) overlay.classList.add('is-visible');
    }
    toggles.forEach(function (toggle) {
      toggle.addEventListener('click', function () {
        drawer.classList.contains('is-open') ? close() : open();
      });
    });
    drawer.querySelectorAll('[data-drawer-close]').forEach(function (btn) {
      btn.addEventListener('click', close);
    });
    if (overlay) overlay.addEventListener('click', close);
    document.addEventListener('keydown', function (e) {
      if (e.key === 'Escape' && drawer.classList.contains('is-open')) close();
    });
    WallZ.closeMobileNav = close;
  }

  /* ---------------------------------------------------------------------
   * <quantity-input> — reusable stepper used on product forms & cart lines.
   * ------------------------------------------------------------------- */
  if (!customElements.get('quantity-input')) {
    class QuantityInput extends HTMLElement {
      connectedCallback() {
        this.input = this.querySelector('input');
        this.querySelectorAll('button').forEach((btn) =>
          btn.addEventListener('click', this.onButtonClick.bind(this))
        );
      }
      onButtonClick(event) {
        event.preventDefault();
        var min = parseInt(this.input.min, 10) || 1;
        var max = this.input.max ? parseInt(this.input.max, 10) : Infinity;
        var current = parseInt(this.input.value, 10) || min;
        var next = event.currentTarget.name === 'plus' ? current + 1 : current - 1;
        next = Math.min(Math.max(next, min), max);
        if (next !== current) {
          this.input.value = next;
          this.input.dispatchEvent(new Event('change', { bubbles: true }));
        }
      }
    }
    customElements.define('quantity-input', QuantityInput);
  }

  /* ---------------------------------------------------------------------
   * Product recommendations — used on the product page ("You May Also
   * Like") and the cart page ("You might also like"). Both just drop a
   * [data-recommendations] container with a data-url pointing at the
   * Section Rendering API; this fetches once per container on page load.
   * ------------------------------------------------------------------- */
  function initRecommendations() {
    document.querySelectorAll('[data-recommendations]').forEach(function (wrap) {
      var url = wrap.dataset.url;
      if (!url || url.indexOf('product_id=') === -1 || url.trim().endsWith('product_id=')) return;
      fetch(url)
        .then(function (res) { return res.text(); })
        .then(function (html) {
          var doc = new DOMParser().parseFromString(html, 'text/html');
          var section = doc.querySelector('[data-recommendations-section]');
          if (section && section.innerHTML.trim()) wrap.innerHTML = section.innerHTML;
        })
        .catch(function () {});
    });
  }

  /* ---------------------------------------------------------------------
   * Boot
   * ------------------------------------------------------------------- */
  document.addEventListener('DOMContentLoaded', function () {
    initStickyHeader();
    initScrollReveal();
    initAnnouncementBar();
    initMobileNav();
    initRecommendations();
  });
})();
